import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, CheckCircle2, RefreshCcw, Trash2, Save, FlaskConical, Database,
} from "lucide-react";
import { PageShell, PageStat } from "@/components/page-shell";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { isChatModel } from "@/lib/ai-model-capabilities";
// ──────────────────────────────────────────────────────────────────────
// Org-level "AI Providers" settings page (tenant admins).
// Covers: provider selection, API key (write-only — never displayed),
// model detection, display name, base URL override, Azure resource fields,
// allowed bots, daily spend cap, embedding provider/model.
//
// AiModelPanel — reusable card/form (no PageShell) embedded in tabs.
// AiProvidersPage — standalone page wrapping AiModelPanel in PageShell.
// ──────────────────────────────────────────────────────────────────────

interface AiProviderInfo {
  id: string;
  name: string;
  keyPlaceholder: string;
  keyHelpUrl: string;
  fallbackModels: string[];
}

interface Integration {
  isDefault: boolean;
  approvalStatus?: string;
  configured: boolean;
  provider: string | null;
  providerName: string | null;
  apiKey: string; // masked preview only — never the real key
  hasKey: boolean;
  model: string | null;
  temperature: number;
  maxTokens: number;
  availableModels: string[];
  // Extended fields
  displayName: string | null;
  baseUrlOverride: string | null;
  azureTenantId: string | null;
  azureResourceName: string | null;
  azureDeploymentId: string | null;
  allowedBotIds: string[] | null;
  dailySpendCapUsd: number | null;
  todaySpendUsd: number;
  // Embedding
  embeddingProvider: string | null;
  embeddingModel: string | null;
}

interface Chatbot { id: string; name: string; }

// OpenAI embedding models (only supported embedding service today)
const EMBEDDING_MODELS: Record<string, string[]> = {
  openai: [
    "text-embedding-3-small",
    "text-embedding-3-large",
    "text-embedding-ada-002",
  ],
};

export const INTEGRATION_KEY = ["/api/organization/ai-integration"] as const;

// ── AiModelPanel ─────────────────────────────────────────────────────
// Reusable inner panel — all state/mutations + the Card UI.
// No PageShell wrapper so it can be embedded in tabs or standalone pages.
export function AiModelPanel() {
  const { toast } = useToast();
  const { data: providers } = useQuery<AiProviderInfo[]>({ queryKey: ["/api/ai-providers"] });
  const { data: integration, isLoading } = useQuery<Integration>({ queryKey: INTEGRATION_KEY });
  const { data: chatbots } = useQuery<Chatbot[]>({ queryKey: ["/api/chatbots"] });

  // ── Form state ──────────────────────────────────────────────────────
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState(0.3);
  const [maxTokens, setMaxTokens] = useState(1000);
  const [models, setModels] = useState<string[]>([]);
  // Extended
  const [displayName, setDisplayName] = useState("");
  const [baseUrlOverride, setBaseUrlOverride] = useState("");
  const [azureTenantId, setAzureTenantId] = useState("");
  const [azureResourceName, setAzureResourceName] = useState("");
  const [azureDeploymentId, setAzureDeploymentId] = useState("");
  const [allowedBotIds, setAllowedBotIds] = useState<string[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [dailySpendCapUsd, setDailySpendCapUsd] = useState("");
  // Embedding
  const [embeddingProvider, setEmbeddingProvider] = useState("");
  const [embeddingModel, setEmbeddingModel] = useState("");

  // Hydrate form when saved integration loads.
  useEffect(() => {
    if (!integration) return;
    setProvider(integration.provider ?? "");
    setModel(integration.model ?? "");
    setTemperature(integration.temperature ?? 0.3);
    setMaxTokens(integration.maxTokens ?? 1000);
    setModels(integration.availableModels ?? []);
    setApiKey(""); // never prefill the secret
    setDisplayName(integration.displayName ?? "");
    setBaseUrlOverride(integration.baseUrlOverride ?? "");
    setAzureTenantId(integration.azureTenantId ?? "");
    setAzureResourceName(integration.azureResourceName ?? "");
    setAzureDeploymentId(integration.azureDeploymentId ?? "");
    setAllowedBotIds(integration.allowedBotIds ?? []);
    setIsDefault(integration.isDefault ?? false);
    setDailySpendCapUsd(integration.dailySpendCapUsd != null ? String(integration.dailySpendCapUsd) : "");
    setEmbeddingProvider(integration.embeddingProvider ?? "");
    setEmbeddingModel(integration.embeddingModel ?? "");
  }, [integration]);

  const selectedInfo = providers?.find((p) => p.id === provider);
  const providerChanged = !!integration?.provider && provider !== integration.provider;
  const hasUsableKey = !!apiKey || (integration?.hasKey && !providerChanged);
  const isAzure = provider === "azure-openai";

  const invalidate = () => queryClient.invalidateQueries({ queryKey: INTEGRATION_KEY });

  // ── Mutations ────────────────────────────────────────────────────────
  const detectMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/organization/ai-integration/detect", {
        provider,
        apiKey: apiKey || undefined,
      });
      return res.json() as Promise<{ provider: string; models: string[]; detected: boolean }>;
    },
    onSuccess: (data) => {
      setModels(data.models ?? []);
      if (data.models?.length && !data.models.includes(model)) setModel(data.models[0]);
      toast({
        title: data.detected ? "Models detected" : "Showing known models",
        description: `${data.models?.length ?? 0} model${data.models?.length === 1 ? "" : "s"} available`,
      });
    },
    onError: (err: Error) => toast({ title: "Detect failed", description: err.message, variant: "destructive" }),
  });

  const testMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/organization/ai-integration/detect", {
        provider,
        apiKey: apiKey || undefined,
      });
      return res.json() as Promise<{ provider: string; models: string[]; detected: boolean }>;
    },
    onSuccess: (data) => {
      if (data.detected) {
        toast({ title: "Provider test passed", description: `Connected to ${selectedInfo?.name}. ${data.models.length} model(s) found.` });
        setModels(data.models);
        if (data.models?.length && !data.models.includes(model)) setModel(data.models[0]);
      } else {
        toast({ title: "Provider test: using fallback list", description: "Could not live-verify the key — showing cached model list.", variant: "destructive" });
      }
    },
    onError: (err: Error) => toast({ title: "Test failed", description: err.message, variant: "destructive" }),
  });

  const buildSavePayload = () => ({
    isDefault,
    provider,
    apiKey: apiKey || undefined,
    model: model || undefined,
    temperature,
    maxTokens,
    displayName: displayName || null,
    baseUrlOverride: baseUrlOverride || "",
    azureTenantId: azureTenantId || null,
    azureResourceName: azureResourceName || null,
    azureDeploymentId: azureDeploymentId || null,
    allowedBotIds: allowedBotIds.length > 0 ? allowedBotIds : [],
    dailySpendCapUsd: dailySpendCapUsd ? parseFloat(dailySpendCapUsd) : null,
    embeddingProvider: embeddingProvider || "",
    embeddingModel: embeddingModel || "",
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/organization/ai-integration", buildSavePayload());
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "AI integration saved", description: "Submitted credentials require approval before use. Default and allowed-bot settings control chatbot fallback." });
      invalidate();
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const clearMut = useMutation({
    mutationFn: async () => apiRequest("DELETE", "/api/organization/ai-integration"),
    onSuccess: () => {
      toast({ title: "AI integration cleared" });
      setProvider(""); setApiKey(""); setModel(""); setModels([]);
      setTemperature(0.3); setMaxTokens(1000); setDisplayName("");
      setBaseUrlOverride(""); setAzureTenantId(""); setAzureResourceName("");
      setAzureDeploymentId(""); setAllowedBotIds([]); setDailySpendCapUsd("");
      setEmbeddingProvider(""); setEmbeddingModel(""); setIsDefault(false);
      invalidate();
    },
    onError: (err: Error) => toast({ title: "Clear failed", description: err.message, variant: "destructive" }),
  });

  // Merge detected models with saved model + provider fallbacks.
  const modelOptions = Array.from(
    new Set([
      ...models,
      ...(selectedInfo?.fallbackModels ?? []),
      ...(model ? [model] : []),
    ].filter(m => m && isChatModel(provider, m))),
  );

  const embeddingModelOptions = EMBEDDING_MODELS[embeddingProvider] ?? [];

  const toggleBot = (botId: string) => {
    setAllowedBotIds((prev) =>
      prev.includes(botId) ? prev.filter((id) => id !== botId) : [...prev, botId]
    );
  };

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <Card data-testid="card-ai-integration">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-[#1FA8E0]" /> Organization AI model
            </CardTitle>
            <CardDescription>
              Configure the organization's approved AI provider for built-in AI features and, when enabled and allowed, as a fallback for customer-facing chatbots.
            </CardDescription>
          </div>
          {integration?.configured && (
            <Badge variant="default" data-testid="badge-integration-status">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ── Provider ─────────────────────────────────────────── */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold" htmlFor="select-ai-provider">Provider</Label>
          <Select
            value={provider}
            onValueChange={(v) => {
              setProvider(v);
              setApiKey("");
              setModels([]);
              setModel("");
            }}
          >
            <SelectTrigger id="select-ai-provider" className="max-w-sm" data-testid="select-provider">
              <SelectValue placeholder="Select a provider" />
            </SelectTrigger>
            <SelectContent>
              {providers?.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`option-provider-${p.id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {providers?.length ?? 0} providers available:{" "}
            {providers?.map((p) => p.name).join(", ")}
          </p>
        </div>

        {provider && (
          <>
            {/* ── Display Name ────────────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <Label className="text-sm font-semibold" htmlFor="input-ai-display-name">Display Name</Label>
              <Input
                id="input-ai-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={`e.g. "University ${selectedInfo?.name}"`}
                data-testid="input-display-name"
              />
              <p className="text-xs text-muted-foreground">
                Human-friendly label shown in the provider list. Leave blank to use the provider name.
              </p>
            </div>

            {/* ── API Key (write-only) ─────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <Label className="text-sm font-semibold" htmlFor="input-ai-api-key">API key</Label>
              {integration?.hasKey && !providerChanged && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="text-key-configured">
                  <span className="tracking-widest">••••••••••••</span>
                  <Badge variant="outline" className="text-xs">Configured</Badge>
                </div>
              )}
              <Input
                id="input-ai-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  integration?.hasKey && !providerChanged
                    ? "Enter a new key to replace the saved one"
                    : selectedInfo?.keyPlaceholder ?? "Enter API key"
                }
                data-testid="input-api-key"
              />
              {selectedInfo?.keyHelpUrl && (
                <a
                  href={selectedInfo.keyHelpUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Where do I find this key? ↗
                </a>
              )}
            </div>

            {/* ── Test Provider ────────────────────────────────── */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasUsableKey || testMut.isPending}
                onClick={() => testMut.mutate()}
                data-testid="button-test-provider"
              >
                <FlaskConical className={`w-3.5 h-3.5 mr-1 ${testMut.isPending ? "animate-spin" : ""}`} />
                {testMut.isPending ? "Testing…" : "Test provider"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasUsableKey || detectMut.isPending}
                onClick={() => detectMut.mutate()}
                data-testid="button-detect-models"
              >
                <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${detectMut.isPending ? "animate-spin" : ""}`} />
                {detectMut.isPending ? "Detecting…" : "Detect models"}
              </Button>
            </div>

            {/* ── Model Version ────────────────────────────────── */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold" htmlFor="select-ai-model">Model version</Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Select value={model} onValueChange={setModel} disabled={modelOptions.length === 0}>
                  <SelectTrigger id="select-ai-model" className="max-w-sm" data-testid="select-model">
                    <SelectValue placeholder="Detect or pick a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((m) => (
                      <SelectItem key={m} value={m} data-testid={`option-model-${m}`}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Add the API key first, then click "Detect models" to load the latest versions.
              </p>
            </div>

            {/* ── Base URL Override ────────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <Label className="text-sm font-semibold" htmlFor="input-ai-base-url">Base URL Override</Label>
              <Input
                id="input-ai-base-url"
                value={baseUrlOverride}
                onChange={(e) => setBaseUrlOverride(e.target.value)}
                placeholder="https://your-proxy.example.com/v1"
                data-testid="input-base-url"
              />
              <p className="text-xs text-muted-foreground">
                HTTPS only. Leave blank to use the provider's default endpoint. Used for Azure OpenAI, private proxies, or OpenAI-compatible endpoints.
              </p>
            </div>

            {/* ── Azure OpenAI fields ──────────────────────────── */}
            {isAzure && (
              <div className="space-y-4 p-4 rounded-lg border border-dashed" data-testid="section-azure">
                <p className="text-sm font-semibold">Azure OpenAI resource details</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="input-azure-tenant-id">Azure Tenant ID</Label>
                    <Input
                      id="input-azure-tenant-id"
                      value={azureTenantId}
                      onChange={(e) => setAzureTenantId(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      data-testid="input-azure-tenant-id"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="input-azure-resource-name">Resource Name</Label>
                    <Input
                      id="input-azure-resource-name"
                      value={azureResourceName}
                      onChange={(e) => setAzureResourceName(e.target.value)}
                      placeholder="my-azure-openai-resource"
                      data-testid="input-azure-resource-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="input-azure-deployment-id">Deployment Name</Label>
                    <Input
                      id="input-azure-deployment-id"
                      value={azureDeploymentId}
                      onChange={(e) => setAzureDeploymentId(e.target.value)}
                      placeholder="gpt-4o-deployment"
                      data-testid="input-azure-deployment-id"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Set the Base URL Override above to your Azure endpoint (e.g. https://&lt;resource&gt;.openai.azure.com/openai/deployments/&lt;deployment&gt;).
                </p>
              </div>
            )}

            {/* ── Default Provider toggle ──────────────────────── */}
            <div className="flex items-center justify-between max-w-sm rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-semibold">Default provider</Label>
                <p className="text-xs text-muted-foreground">
                  Allow this provider as the default fallback for eligible chatbots. This choice is preserved when changing provider; replacement keys require approval.
                </p>
              </div>
              <Switch
                checked={isDefault}
                onCheckedChange={setIsDefault}
                data-testid="switch-default-provider"
              />
            </div>

            {/* ── Allowed Bots ─────────────────────────────────── */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Allowed Bots</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Choose which chatbots may use this organization provider. Leave all unchecked to allow all chatbots in this organization.
              </p>
              {chatbots && chatbots.length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="section-allowed-bots">
                  {chatbots.map((bot) => (
                    <button
                      key={bot.id}
                      type="button"
                      onClick={() => toggleBot(bot.id)}
                      data-testid={`toggle-bot-${bot.id}`}
                      className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                        allowedBotIds.includes(bot.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary/50"
                      }`}
                    >
                      {bot.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No bots found in this organization.</p>
              )}
            </div>

            {/* ── Daily Spend Cap ──────────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <Label className="text-sm font-semibold">Daily Spend Cap (USD)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={dailySpendCapUsd}
                onChange={(e) => setDailySpendCapUsd(e.target.value)}
                placeholder="e.g. 10.00"
                data-testid="input-daily-spend-cap"
              />
              <p className="text-xs text-muted-foreground">
                Soft cap in USD per UTC day. Chat requests are blocked (HTTP 402) once the cap is reached. Leave blank for no cap.
                {integration?.todaySpendUsd != null && (
                  <> Today so far: ${Number(integration.todaySpendUsd).toFixed(4)}.</>
                )}
              </p>
            </div>

            {/* ── Temperature ──────────────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Temperature</Label>
                <span className="text-sm tabular-nums" data-testid="text-temperature">
                  {temperature.toFixed(2)}
                </span>
              </div>
              <Slider
                min={0} max={2} step={0.05}
                value={[temperature]}
                onValueChange={(v) => setTemperature(v[0])}
                aria-label="Temperature"
                data-testid="slider-temperature"
              />
              <p className="text-xs text-muted-foreground">
                Lower is more focused and deterministic; higher is more creative.
              </p>
            </div>

            {/* ── Max Tokens ───────────────────────────────────── */}
            <div className="space-y-2 max-w-sm">
              <Label className="text-sm font-semibold">Max tokens</Label>
              <Input
                type="number" min={1} max={32000}
                value={maxTokens}
                onChange={(e) => setMaxTokens(Number(e.target.value))}
                data-testid="input-max-tokens"
              />
              <p className="text-xs text-muted-foreground">Upper bound on the length of each AI response.</p>
            </div>

            {/* ── Embedding Provider/Model ─────────────────────── */}
            <div className="space-y-4 p-4 rounded-lg border border-dashed" data-testid="section-embedding">
              <div>
                <p className="text-sm font-semibold">Knowledge Embedding</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Use each chatbot's effective approved provider or select a specific provider with an eligible credential. Indexing and queries use the same profile. Unsupported embedding providers use keyword retrieval only. Changes require re-ingesting knowledge sources.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Embedding Provider</Label>
                  <Select
                    value={embeddingProvider || "__none__"}
                    onValueChange={(v) => { setEmbeddingProvider(v === "__none__" ? "" : v); setEmbeddingModel(""); }}
                  >
                    <SelectTrigger data-testid="select-embedding-provider">
                      <SelectValue placeholder="Same as effective chatbot provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Same as effective chatbot provider</SelectItem>
                      {Object.keys(EMBEDDING_MODELS).map((ep) => (
                        <SelectItem key={ep} value={ep} data-testid={`option-embedding-provider-${ep}`}>
                          {providers?.find((p) => p.id === ep)?.name ?? ep}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Embedding Model</Label>
                  <Select
                    value={embeddingModel || "__none__"}
                    onValueChange={(v) => setEmbeddingModel(v === "__none__" ? "" : v)}
                    disabled={!embeddingProvider && !provider}
                  >
                    <SelectTrigger data-testid="select-embedding-model">
                      <SelectValue placeholder="Default for provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Default for provider</SelectItem>
                      {(embeddingModelOptions.length > 0
                        ? embeddingModelOptions
                        : (EMBEDDING_MODELS[provider] ?? [])
                      ).map((m) => (
                        <SelectItem key={m} value={m} data-testid={`option-embedding-model-${m}`}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* ── Actions ──────────────────────────────────────── */}
            <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <Button
                variant="ghost" size="sm"
                className="w-full sm:w-auto text-rose-600 hover:text-rose-700"
                disabled={!integration?.configured || clearMut.isPending}
                onClick={() => {
                  if (confirm("Disconnect the organization's AI model? This will clear all provider settings.")) clearMut.mutate();
                }}
                data-testid="button-clear-integration"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
              <Button
                size="sm"
                className="w-full sm:w-auto"
                disabled={!provider || !hasUsableKey || saveMut.isPending}
                onClick={() => saveMut.mutate()}
                data-testid="button-save-integration"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {saveMut.isPending ? "Saving…" : "Save integration"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── AiProvidersPage ──────────────────────────────────────────────────
// Standalone page wrapper — kept for the /ai-providers route redirect.
export default function AiProvidersPage() {
  const { data: integration } = useQuery<Integration>({ queryKey: INTEGRATION_KEY });

  return (
    <PageShell
      title={
        <span className="flex items-center gap-2" data-testid="heading-ai-integration">
          <Sparkles className="w-5 h-5 text-[#1FA8E0]" /> AI Providers
        </span>
      }
      subtitle="Configure your organization's AI provider for built-in AI features (Setup Agent, Help Assistant) and knowledge embeddings."
      stats={
        <>
          <PageStat
            label="Status"
            value={integration?.configured ? "Connected" : "Not set"}
            hint={integration?.displayName ?? integration?.providerName ?? "No provider yet"}
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
          <PageStat
            label="Active model"
            value={integration?.configured && integration.model ? integration.model : "—"}
            hint="Used by platform AI"
            icon={<Sparkles className="w-4 h-4" />}
          />
          <PageStat
            label="Today's spend"
            value={integration?.todaySpendUsd != null ? `$${Number(integration.todaySpendUsd).toFixed(4)}` : "—"}
            hint={integration?.dailySpendCapUsd ? `Cap: $${integration.dailySpendCapUsd}` : "No cap set"}
            icon={<Database className="w-4 h-4" />}
          />
        </>
      }
    >
      <AiModelPanel />
    </PageShell>
  );
}
